import { observable } from "mobx";
import { pathToRegexp, match } from "path-to-regexp";
import { Navigation } from "./Navigation";
import { AnyLocation } from "./Location";
import { LocationFactory } from "./utils";

export type OnCloseHandler = () => void;

export class NavigationManager {
  @observable private instances: Record<string, Navigation> = {};
  private onCloseHandlers: Record<string, OnCloseHandler[]> = {};

  private directUrlFactories: Array<{
    path: string;
    re: RegExp;
    locationFactory: LocationFactory;
  }> = [];

  private createUniqueInstanceName = (name: string, index: number) => {
    return [name, index].join("_");
  };

  registerDirectUrl(path: string, locationFactory: LocationFactory) {
    this.directUrlFactories.push({ path, locationFactory, re: pathToRegexp(path) });
  }

  maybeTriggerDirectUrl() {
    const url = window.location.pathname;
    const matchingDirectUrl = this.directUrlFactories.find(({ re, locationFactory }) => {
      return !!url.match(re);
    });

    if (!matchingDirectUrl) {
      return;
    }

    const matchFn = match(matchingDirectUrl.path);
    const matched = matchFn(url);

    if (!matched) {
      return;
    }

    const location = matchingDirectUrl.locationFactory(matched.params);
    this.open("open_on_startup", location);
  }

  open = (name: string, initialLocation: AnyLocation, baseUri?: string) => {
    if (!initialLocation) {
      return;
    }

    //always open new instance if going to loop navigation
    const _name = this.instances[name]
      ? this.createUniqueInstanceName(name, this.getInstances().length)
      : name;

    const instance = this.instances[_name] || this.createInstance(_name, baseUri);
    instance.navigate(initialLocation);
    return instance;
  };

  // todo: remove, check Navigation.ts afterClose
  on(event: "close", instance: string, handler: OnCloseHandler) {
    if (event === "close") {
      this.onCloseHandlers[instance] = this.onCloseHandlers[instance] || [];
      this.onCloseHandlers[instance].push(handler);
    }
  }

  close = (name: string) => this.clearInstance(name);

  createInstance = (name: string, baseUri = window.location.pathname) => {
    // if (this.instances[0].isBlocked)
    this.instances[name] = new Navigation(name, baseUri, () => this.clearInstance(name));
    return this.instances[name];
  };

  clearInstance = (name: string) => {
    const instance = this.getInstance(name);
    instance.clear();
    this.onCloseHandlers[name]?.forEach((x) => x());
    this.onCloseHandlers[name] = [];
  };

  hasInstance = (name: string) => Boolean(this.instances[name]);

  getInstance = (name: string) => {
    if (!this.instances[name]) {
      throw new Error(`Instance ${name} doesn't exist`);
    }

    return this.instances[name];
  };

  hasAnyInstance = (name: string) => {
    const instance = this.getAnyInstance(name);
    return Boolean(instance);
  };

  getAnyInstance = (name: string) => {
    // handle instance name with index like "name_0", check createUniqueInstanceName function
    const instance = this.getInstances().find((instance) => instance.name.includes(name));

    if (!instance) {
      throw new Error(`Instance ${name} doesn't exist`);
    }

    return instance;
  };

  getInstances = () =>
    Object.keys(this.instances)
      .map((key) => this.instances[key])
      .filter((x) => !!x.location);
}
